import { z } from 'zod';

import { cuidLike, nonEmptyString } from '../zod-common';

import { MODERATION_CATEGORIES } from './chat';

export const SUBSCRIPTION_PLANS = ['FREE', 'PLUS', 'PREMIUM', 'PRO', 'BUSINESS'] as const;
export type SubscriptionPlanValue = (typeof SUBSCRIPTION_PLANS)[number];

export const COMMISSION_ROLES = ['REQUESTER', 'BIDDER'] as const;
export type CommissionRoleValue = (typeof COMMISSION_ROLES)[number];

export interface PlanQuotaValues {
  initialPerMonth: number;
  repromptPerOffer: number;
  isInitialUnlimited: boolean;
}

export const DEFAULT_PLAN_QUOTAS: Record<SubscriptionPlanValue, PlanQuotaValues> = {
  FREE: { initialPerMonth: 3, repromptPerOffer: 5, isInitialUnlimited: false },
  PLUS: { initialPerMonth: 10, repromptPerOffer: 10, isInitialUnlimited: false },
  PREMIUM: { initialPerMonth: 50, repromptPerOffer: 20, isInitialUnlimited: true },
  PRO: { initialPerMonth: 50, repromptPerOffer: 20, isInitialUnlimited: true },
  BUSINESS: { initialPerMonth: 50, repromptPerOffer: 20, isInitialUnlimited: true },
};

export const adminUpdatePlanQuotaSchema = z.object({
  plan: z.enum(SUBSCRIPTION_PLANS),
  initialPerMonth: z.number().int().min(0).max(1000),
  repromptPerOffer: z.number().int().min(0).max(1000),
  isInitialUnlimited: z.boolean(),
});
export type AdminUpdatePlanQuotaInput = z.infer<typeof adminUpdatePlanQuotaSchema>;

export const adminApproveBidderVerificationSchema = z.object({
  profileId: cuidLike,
  comment: z.string().trim().max(2_000).optional(),
});
export type AdminApproveBidderVerificationInput = z.infer<
  typeof adminApproveBidderVerificationSchema
>;

export const adminRejectBidderVerificationSchema = z.object({
  profileId: cuidLike,
  reason: nonEmptyString.max(2_000),
});
export type AdminRejectBidderVerificationInput = z.infer<
  typeof adminRejectBidderVerificationSchema
>;

export const adminSuspendUserSchema = z.object({
  userId: cuidLike,
  durationDays: z.number().int().positive().max(3650).optional(),
  reason: nonEmptyString.max(2_000),
});
export type AdminSuspendUserInput = z.infer<typeof adminSuspendUserSchema>;

export const adminUnsuspendUserSchema = z.object({
  userId: cuidLike,
});
export type AdminUnsuspendUserInput = z.infer<typeof adminUnsuspendUserSchema>;

export const adminBanUserSchema = z.object({
  userId: cuidLike,
  reason: nonEmptyString.max(2_000),
});
export type AdminBanUserInput = z.infer<typeof adminBanUserSchema>;

export const adminRescoreBidSchema = z.object({
  bidId: cuidLike,
});
export type AdminRescoreBidInput = z.infer<typeof adminRescoreBidSchema>;

export const adminGrantPlanSchema = z.object({
  userId: cuidLike,
  plan: z.enum(SUBSCRIPTION_PLANS),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().optional(),
  reason: nonEmptyString.max(2_000),
});
export type AdminGrantPlanInput = z.infer<typeof adminGrantPlanSchema>;

export const adminRevokePlanGrantSchema = z.object({
  grantId: cuidLike,
});
export type AdminRevokePlanGrantInput = z.infer<typeof adminRevokePlanGrantSchema>;

export const adminCreateCommissionOverrideSchema = z
  .object({
    userId: cuidLike,
    role: z.enum(COMMISSION_ROLES),
    rate: z.number(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().optional(),
    reason: nonEmptyString.max(2_000),
  })
  .superRefine((data, ctx) => {
    if (data.role === 'REQUESTER' && (data.rate < 0 || data.rate > 0.1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'REQUESTER rate must be in [0, 0.10]',
        path: ['rate'],
      });
    }
    if (data.role === 'BIDDER' && (data.rate < 0 || data.rate > 0.05)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'BIDDER rate must be in [0, 0.05]',
        path: ['rate'],
      });
    }
  });
export type AdminCreateCommissionOverrideInput = z.infer<
  typeof adminCreateCommissionOverrideSchema
>;

export const adminDeleteCommissionOverrideSchema = z.object({
  overrideId: cuidLike,
});
export type AdminDeleteCommissionOverrideInput = z.infer<
  typeof adminDeleteCommissionOverrideSchema
>;

export const adminHideReportMessageSchema = z.object({
  reportId: cuidLike,
  category: z.enum(MODERATION_CATEGORIES).default('OTHER'),
  sanction: z.boolean().default(false),
});
export type AdminHideReportMessageInput = z.infer<typeof adminHideReportMessageSchema>;

export const adminDismissReportSchema = z.object({
  reportId: cuidLike,
});
export type AdminDismissReportInput = z.infer<typeof adminDismissReportSchema>;

export const adminUserListSchema = z
  .object({
    search: z.string().trim().max(100).optional(),
    status: z.enum(['PENDING', 'ACTIVE', 'SUSPENDED', 'BLOCKED', 'BANNED', 'DELETED']).optional(),
    limit: z.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
  })
  .strict();
export type AdminUserListInput = z.infer<typeof adminUserListSchema>;

export const adminVerificationListSchema = z
  .object({
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type AdminVerificationListInput = z.infer<typeof adminVerificationListSchema>;

export const adminGrantListSchema = z
  .object({
    userId: cuidLike.optional(),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type AdminGrantListInput = z.infer<typeof adminGrantListSchema>;

export const adminOverrideListSchema = z
  .object({
    userId: cuidLike.optional(),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type AdminOverrideListInput = z.infer<typeof adminOverrideListSchema>;

export const adminScoringListSchema = z
  .object({
    threshold: z.number().int().min(0).max(100).default(40),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type AdminScoringListInput = z.infer<typeof adminScoringListSchema>;

export const adminMessageReportListSchema = z
  .object({
    limit: z.number().int().min(1).max(100).default(50),
    status: z.enum(['PENDING', 'REVIEWED', 'DISMISSED']).default('PENDING'),
  })
  .strict();
export type AdminMessageReportListInput = z.infer<typeof adminMessageReportListSchema>;

export const adminUnhideReportMessageSchema = z.object({
  reportId: cuidLike,
});
export type AdminUnhideReportMessageInput = z.infer<typeof adminUnhideReportMessageSchema>;

export const adminGhostFlagListSchema = z
  .object({
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type AdminGhostFlagListInput = z.infer<typeof adminGhostFlagListSchema>;

export const adminApproveGhostFlagSchema = z.object({
  projectId: cuidLike,
  comment: z.string().trim().max(2_000).optional(),
});
export type AdminApproveGhostFlagInput = z.infer<typeof adminApproveGhostFlagSchema>;

export const adminRejectGhostFlagSchema = z.object({
  projectId: cuidLike,
  reason: nonEmptyString.max(2_000),
});
export type AdminRejectGhostFlagInput = z.infer<typeof adminRejectGhostFlagSchema>;
